// src/components/list/SensorItem.tsx

import { MemoryTwoTone } from '@mui/icons-material'
import {
  Avatar,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Switch,
} from '@mui/material'

import {
  DeviceId,
  MeterNumber,
  SensorId,
  SensorName,
  SensorProvider,
  SensorTarget,
  SensorType,
  SubSensorTarget,
  TypeInFolder,
} from '@/types/domain'

import { categoryByType, normalizedSensorType, SensorIcon } from '../../api'
import { svgConstants as SVG } from '../../constants'
import {
  OperatorData,
  useDeviceId,
  useMeterNumber,
  useOperator,
  useSensorName,
  useSensorProvider,
  useSensorTarget,
  useSensorType,
  useSubSensorTarget,
  useTypeInFolder,
} from '../../hooks'

type Props = {
  id: SensorId
  onClick?: () => void
  selected?: boolean
}

export const SensorItem = ({ id, onClick, selected }: Props) => {
  const opData: OperatorData = useOperator()
  const isAdmin = opData?.isAdmin

  const deviceId: DeviceId = useDeviceId(id)
  const provider: SensorProvider = useSensorProvider(id)
  const name: SensorName = useSensorName(id)
  const subTarget: SubSensorTarget = useSubSensorTarget(id)
  const target: SensorTarget = useSensorTarget(id)
  const tif: TypeInFolder = useTypeInFolder(id)
  const type: SensorType = useSensorType(id)
  const meter: MeterNumber = useMeterNumber(id)

  let imageUrl = SensorIcon(categoryByType(type, tif))
  const normalized = normalizedSensorType(type, tif)

  switch (normalized) {
    case 'energy':
      imageUrl = SVG.MEDIUM_ENERGY
      break
    case 'water':
      imageUrl = SVG.MEDIUM_WATER
      break
    case 'gas':
      imageUrl = SVG.MEDIUM_HEATING
      break
    default:
      imageUrl && <MemoryTwoTone />
      break
  }

  const adminInfo = isAdmin ? `Sensor ID: ${id}` : undefined

  return (
    <ListItemButton onClick={onClick}>
      <ListItemAvatar>
        <Avatar src={imageUrl}>{!imageUrl && <MemoryTwoTone />}</Avatar>
      </ListItemAvatar>

      <ListItemText
        sx={{ width: 0.5 }}
        primary={`Name: ${name}`}
        secondary={adminInfo}
      />

      <ListItemText
        sx={{ width: 0.25 }}
        primary={'Zählernummer'}
        secondary={meter}
      />

      {isAdmin && (
        <ListItemText
          sx={{ width: 0.25 }}
          primary={'Typ Casting'}
          secondary={tif}
        />
      )}

      {isAdmin && (
        <ListItemText
          sx={{ width: 0.25 }}
          primary={'Target'}
          secondary={`${target}::${subTarget}`}
        />
      )}

      {isAdmin && (
        <ListItemText
          sx={{ width: 0.5 }}
          primary={'Hardware Information'}
          secondary={`${provider}::${deviceId}::${type}`}
        />
      )}

      <ListItemIcon>
        <Switch checked={selected} />
      </ListItemIcon>
    </ListItemButton>
  )
}
